import React from 'react';
import { useTranslation } from 'react-i18next';
import { ZapIcon, BarChart3Icon, CheckCircleIcon, Loader2 } from 'lucide-react';

const fmt = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString());
const pct = (p) => (p == null || Number.isNaN(Number(p)) ? '—' : `${Number(p).toFixed(1)}%`);

const OPERATION_KEYS = ['addition', 'subtraction', 'mixed', 'multiplication', 'division'];
const DIFFICULTY_KEYS = ['easy', 'medium', 'hard'];

const QUALITY = {
  rose: {
    bar: 'bg-gradient-to-r from-rose-400 to-rose-600 shadow-sm shadow-rose-500/30',
    track: 'bg-rose-100/90 ring-1 ring-rose-200/60',
    pill: 'bg-rose-100 text-rose-900 ring-1 ring-rose-200/80',
    value: 'text-rose-800',
  },
  amber: {
    bar: 'bg-gradient-to-r from-amber-400 to-orange-500 shadow-sm shadow-amber-500/25',
    track: 'bg-amber-100/90 ring-1 ring-amber-200/60',
    pill: 'bg-amber-100 text-amber-950 ring-1 ring-amber-200/80',
    value: 'text-amber-900',
  },
  emerald: {
    bar: 'bg-gradient-to-r from-emerald-400 to-teal-600 shadow-sm shadow-emerald-500/25',
    track: 'bg-emerald-100/90 ring-1 ring-emerald-200/60',
    pill: 'bg-emerald-100 text-emerald-950 ring-1 ring-emerald-200/80',
    value: 'text-emerald-900',
  },
};

function QualityBar({ label, count, percent, tone }) {
  const q = QUALITY[tone] || QUALITY.emerald;
  const w = Math.min(100, Math.max(0, Number(percent) || 0));
  return (
    <div className="rounded-xl border border-slate-200/60 bg-white/80 p-3 shadow-sm ring-1 ring-slate-900/5 backdrop-blur-sm">
      <div className="flex justify-between gap-3 text-xs">
        <span className={`inline-flex max-w-[65%] items-center rounded-lg px-2 py-0.5 font-semibold ${q.pill}`}>{label}</span>
        <span className={`shrink-0 font-bold tabular-nums ${q.value}`}>
          {fmt(count)} · {pct(percent)}
        </span>
      </div>
      <div className={`mt-2.5 h-2.5 overflow-hidden rounded-full ${q.track}`}>
        <div className={`h-full rounded-full transition-all duration-500 ease-out ${q.bar}`} style={{ width: `${w}%` }} />
      </div>
    </div>
  );
}

function SummaryTile({ icon: Icon, label, value, sub, variant }) {
  const variants = {
    blue: {
      card: 'border-sky-200/80 bg-gradient-to-br from-sky-50 via-white to-blue-50/70 ring-sky-500/10',
      glow: 'from-sky-400/20',
      icon: 'bg-sky-500/15 text-sky-800 ring-sky-400/30',
      label: 'text-sky-900/75',
      val: 'text-sky-950',
      sub: 'text-sky-800/90',
    },
    cyan: {
      card: 'border-cyan-200/80 bg-gradient-to-br from-cyan-50 via-white to-sky-50/60 ring-cyan-500/10',
      glow: 'from-cyan-400/20',
      icon: 'bg-cyan-500/15 text-cyan-900 ring-cyan-400/30',
      label: 'text-cyan-950/80',
      val: 'text-cyan-950',
      sub: 'text-cyan-900/85',
    },
  };
  const v = variants[variant] || variants.blue;
  return (
    <div className={`relative overflow-hidden rounded-2xl border p-4 shadow-md ring-1 transition hover:-translate-y-0.5 hover:shadow-lg ${v.card}`}>
      <div className={`pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br ${v.glow} to-transparent opacity-80`} />
      <div className="relative">
        {Icon && (
          <span className={`mb-2 inline-flex h-10 w-10 items-center justify-center rounded-xl ring-1 ${v.icon}`}>
            <Icon className="h-5 w-5" />
          </span>
        )}
        <p className={`text-[11px] font-bold uppercase tracking-wide ${v.label}`}>{label}</p>
        <p className={`mt-1 text-2xl font-black tabular-nums tracking-tight ${v.val}`}>{value}</p>
        {sub && <p className={`mt-1 text-xs font-semibold ${v.sub}`}>{sub}</p>}
      </div>
    </div>
  );
}

function DifficultyCard({ label, sessions, successRate, tone }) {
  const tones = {
    emerald: 'border-emerald-200/80 bg-gradient-to-br from-emerald-50 to-white ring-emerald-500/10',
    amber: 'border-amber-200/80 bg-gradient-to-br from-amber-50 to-white ring-amber-500/10',
    rose: 'border-rose-200/80 bg-gradient-to-br from-rose-50 to-white ring-rose-500/10',
  };
  return (
    <div className={`rounded-2xl border p-4 shadow-sm ring-1 ${tones[tone] || tones.emerald}`}>
      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-600">{label}</p>
      <p className="mt-2 text-xl font-black tabular-nums text-slate-900">{fmt(sessions)}</p>
      <p className="mt-1 text-xs font-semibold text-slate-600">{successRate}</p>
    </div>
  );
}

/**
 * إحصائيات فلاش أنزان — /education/student-dashboard/flash-anzan
 */
const StudentDashboardFlashAnzan = ({ data, loading, isRTL }) => {
  const { t } = useTranslation();

  const fam = data?.metrics;
  const period = data?.period;
  const aq = fam?.answerQuality;
  const periodLabel =
    period?.seasonYearLabel || (period?.start && period?.end ? `${period.start} → ${period.end}` : null);

  const diffTone = { easy: 'emerald', medium: 'amber', hard: 'rose' };

  return (
    <section id="student-dashboard-flash-anzan" className="scroll-mt-24 overflow-visible rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/95 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
      <div className="overflow-hidden rounded-t-[1.2rem] border-b border-sky-200/50 bg-gradient-to-r from-sky-500/15 via-blue-50/70 to-indigo-50/50 px-4 py-3 sm:px-5">
        <div className={`flex flex-wrap items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 text-white shadow-lg shadow-sky-500/35 ring-2 ring-white/50">
            <ZapIcon className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-black tracking-tight text-sky-950 sm:text-lg">{t('dashboard.stats.flashAnzanStats')}</h2>
            {periodLabel && <p className="mt-0.5 truncate text-xs font-semibold text-sky-800/75">{periodLabel}</p>}
          </div>
        </div>
      </div>
      <div className="relative p-4 sm:p-5">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-b-[1.2rem] bg-white/75 backdrop-blur-[2px]">
            <Loader2 className="h-10 w-10 animate-spin text-sky-600" strokeWidth={2.5} aria-hidden />
          </div>
        )}
        {fam ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <SummaryTile
                icon={BarChart3Icon}
                label={t('dashboard.stats.totalSessions')}
                value={fmt(fam.totalSessions)}
                variant="blue"
              />
              <SummaryTile
                icon={CheckCircleIcon}
                label={t('dashboard.stats.completedSessions')}
                value={fmt(fam.completedSessions)}
                sub={t('dashboard.stats.percentOfTotalSessions', { p: pct(fam.completedPercentOfTotal) })}
                variant="cyan"
              />
            </div>

            {fam.byOperation && (
              <div className="mt-6 overflow-x-auto rounded-xl border border-sky-200/60 bg-white shadow-md ring-1 ring-sky-500/10">
                <table className="w-full min-w-[280px] table-fixed text-xs">
                  <thead>
                    <tr className="bg-gradient-to-r from-sky-600 to-blue-700 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm">
                      <th className="py-2.5 ps-3 pe-2 text-start">{t('dashboard.stats.operation')}</th>
                      <th className="py-2.5 px-1 text-end text-white/95">{t('dashboard.stats.totalSessions')}</th>
                      <th className="py-2.5 px-1 text-end text-white/95">{t('dashboard.stats.completedSessions')}</th>
                      <th className="py-2.5 pe-3 ps-1 text-end">{t('dashboard.stats.correctCount')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-sky-100 bg-sky-50/30">
                    {OPERATION_KEYS.map((op, idx) => {
                      const row = fam.byOperation[op];
                      if (!row) return null;
                      return (
                        <tr
                          key={op}
                          className={`transition-colors hover:bg-sky-100/80 ${idx % 2 === 0 ? 'bg-white/80' : 'bg-sky-50/50'}`}
                        >
                          <td className="py-2 ps-3 pe-2 font-semibold leading-tight text-sky-950">
                            {t(`dashboard.stats.operations.${op}`)}
                          </td>
                          <td className="py-2 px-1.5 text-end font-medium tabular-nums text-slate-800">{fmt(row.sessions)}</td>
                          <td className="py-2 px-1.5 text-end font-medium tabular-nums text-slate-800">{fmt(row.completed)}</td>
                          <td className="py-2 pe-3 ps-1.5 text-end font-bold tabular-nums text-emerald-700">{fmt(row.correct)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {aq && (
              <div className="mt-8 space-y-3">
                <h3 className="inline-flex rounded-lg bg-sky-100/90 px-3 py-1 text-xs font-bold uppercase tracking-wide text-sky-950 ring-1 ring-sky-300/50">
                  {t('dashboard.stats.answerQualityCompleted')}
                </h3>
                <QualityBar
                  label={t('users.students.stats.qualityWeakUnder50')}
                  count={aq.weakUnder50Percent?.count}
                  percent={aq.weakUnder50Percent?.percentOfCompleted}
                  tone="rose"
                />
                <QualityBar
                  label={t('users.students.stats.qualityMedium50to69')}
                  count={aq.medium50To69Percent?.count}
                  percent={aq.medium50To69Percent?.percentOfCompleted}
                  tone="amber"
                />
                <QualityBar
                  label={t('users.students.stats.qualityGood70Plus')}
                  count={aq.good70PlusPercent?.count}
                  percent={aq.good70PlusPercent?.percentOfCompleted}
                  tone="emerald"
                />
              </div>
            )}

            {fam.byDifficulty && (
              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                {DIFFICULTY_KEYS.map((d) => {
                  const row = fam.byDifficulty[d];
                  if (!row) return null;
                  return (
                    <DifficultyCard
                      key={d}
                      label={t(`dashboard.stats.difficulty.${d}`)}
                      sessions={row.sessions}
                      successRate={t('dashboard.stats.successRate', { p: pct(row.successRatePercent) })}
                      tone={diffTone[d]}
                    />
                  );
                })}
              </div>
            )}
          </>
        ) : (
          !loading && <p className="text-sm font-semibold text-slate-500">{t('users.students.stats.noData')}</p>
        )}
      </div>
    </section>
  );
};

export default StudentDashboardFlashAnzan;
